import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FaChartLine, FaShoppingCart, FaDollarSign, FaBox, FaUsers, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import LoadingSpinner from '../Orders/LoadingSpinner';
import './Analytics.css';

const Analytics = () => {
  const { Get } = useApi();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await Get('analytics');
      setAnalyticsData(response);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err.response?.status === 404
        ? 'Analytics endpoint not found'
        : err.code === 'ERR_NETWORK'
          ? 'Network error: Please check your connection'
          : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: '#10b981',
      processing: '#8b5cf6',
      pending: '#f59e0b',
      cancelled: '#ef4444'
    };
    return colors[status.toLowerCase()] || '#64748b';
  };

  if (loading) {
    return (
      <div className="analytics-loading">
        <LoadingSpinner />
        <p>Loading analytics data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-error-container">
        <div className="error-card">
          <div className="error-icon">
            <FaExclamationTriangle />
          </div>
          <h3>Unable to Load Analytics</h3>
          <p className="error-message">{error}</p>
          <button onClick={fetchAnalyticsData} className="retry-button">
            <FaSync /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="analytics-empty-container">
        <div className="empty-card">
          <div className="empty-icon">
            <FaChartLine />
          </div>
          <h3>No Analytics Data</h3>
          <p>Analytics data will appear here once you have orders and activity</p>
          <button onClick={fetchAnalyticsData} className="refresh-button">
            <FaSync /> Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      {/* Stats Overview */}
      <div className="analytics-stats-grid">
        <div className="stat-card revenue">
          <div className="stat-icon">
            <FaDollarSign />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Revenue</span>
            <span className="stat-value-analytics">₹{analyticsData.total_revenue?.toLocaleString('en-IN') || '0'}</span>
          </div>
        </div>
        <div className="stat-card orders">
          <div className="stat-icon">
            <FaShoppingCart />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Orders</span>
            <span className="stat-value-analytics">{analyticsData.total_orders || '0'}</span>
          </div>
        </div>
        <div className="stat-card products">
          <div className="stat-icon">
            <FaBox />
          </div>
          <div className="stat-content">
            <span className="stat-label">Products Sold</span>
            <span className="stat-value-analytics">{analyticsData.products_sold || '0'}</span>
          </div>
        </div>
        <div className="stat-card customers">
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-content">
            <span className="stat-label">Customers</span>
            <span className="stat-value-analytics">{analyticsData.total_customers || '0'}</span>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="analytics-charts-row">
        <div className="chart-card">
          <h3><FaChartLine /> Monthly Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analyticsData.monthly_revenue || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#64748b" style={{ fontSize: '12px' }} />
              <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                formatter={(value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value)}
              />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Revenue" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3><FaShoppingCart /> Monthly Orders</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analyticsData.monthly_orders || []} margin={{ top: 10, right: 30, left: 0, bottom: 0 }} barCategoryGap="20%">
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fill: '#64748b', fontSize: 13, fontWeight: 500 }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 13 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  padding: '12px 16px'
                }}
                labelStyle={{ fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}
              />
              <Bar
                dataKey="orders"
                fill="url(#barGradient)"
                radius={[8, 8, 0, 0]}
                maxBarSize={60}
                name="Orders"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="analytics-charts-row">
        <div className="chart-card">
          <h3>Order Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={(analyticsData.order_status || []).map(item => ({
                  name: item.status.charAt(0).toUpperCase() + item.status.slice(1),
                  value: item.percentage,
                  count: item.count,
                  color: getStatusColor(item.status)
                }))}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {(analyticsData.order_status || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name, props) => [`${value.toFixed(1)}% (${props.payload.count} orders)`, name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Recent Orders</h3>
          <div className="recent-orders-list">
            {(analyticsData.recent_orders || []).length === 0 ? (
              <div className="no-orders">
                <FaShoppingCart />
                <p>No recent orders</p>
              </div>
            ) : (
              (analyticsData.recent_orders || []).map((order, idx) => (
                <div key={idx} className="order-item">
                  <div className="order-info">
                    <div className="order-id">#{order.order_id}</div>
                    <div className="customer-name">{order.customer}</div>
                    <div className="order-date">{new Date(order.date).toLocaleDateString()}</div>
                  </div>
                  <div className={`order-status status-${order.status}`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;